import { ExclamationCircleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import OTPInput from 'react-otp-input';

export const Modal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 dark:bg-white/50 bg-black/50 z-50 flex items-center justify-center">
      <div className="bg-white dark:bg-gray-900 rounded-lg p-6 w-full max-w-lg shadow-lg relative">
        <h2 className="text-xl font-bold mb-4">{title}</h2>
        {children}
        <button onClick={onClose} className="absolute top-2 right-2 text-gray-500 hover:text-red-500">✕</button>
      </div>
    </div>
  );
};



export const FormError = ({ message }) => {
  if (!message) return null;

  return (
    <div className='mt-2 p-2 border border-light-error dark:border-dark-error rounded-md bg-light-error/5 dark:bg-dark-error/5'>
      <div className="py-2 flex items-center gap-2 text-sm text-light-error dark:text-dark-error">
        <ExclamationCircleIcon className="h-8 w-8" />
        <span className='text-sm text-light-error dark:text-dark-error'>{message}</span>
      </div>
    </div>

  );
};

export const FormSuccess = ({ message }) => {
  if (!message) return null;

  return (
    <div className='mt-2 p-2 border border-light-secondary dark:border-dark-secondary rounded-md bg-light-secondary/5 dark:bg-dark-secondary/5'>
      <div className="py-2 flex items-center gap-2 text-sm text-light-secondary dark:text-dark-secondary">
        <ExclamationCircleIcon className="h-8 w-8" />
        <span className='text-sm text-light-secondary dark:text-dark-secondary'>{message}</span>
      </div>
    </div>
  )
}


export const ButtonCustom = ({
  children,
  loading = false,
  icon = null,
  variant = 'primary',
  disabled = false,
  className = '',
  ...props
}) => {
  const baseClasses = 'px-4 py-2 rounded flex items-center justify-center gap-2 transition-all duration-200';
  const variantMap = {
    primary: 'bg-light-primary dark:bg-dark-primary text-white hover:bg-light-primary/80 dark:hover:bg-dark-primary/80',
    danger: 'bg-light-error dark:bg-dark-error text-white hover:bg-light-error/80 dark:hover:bg-dark-error/80',
    secondary: 'bg-light-secondary dark:bg-dark-secondary text-white hover:bg-light-secondary/80 dark:hover:bg-dark-secondary/80',
    tertiary: 'bg-light-tertiary dark:bg-dark-tertiary text-white hover:bg-light-tertiary/80 dark:hover:bg-dark-tertiary/80',
    neutral: 'bg-neutral text-black dark:text-white hover:bg-neutral/80'
  };

  const classes = twMerge(
    baseClasses,
    variantMap[variant],
    loading && 'opacity-60 cursor-not-allowed',
    className
  );

  return (
    <button
      disabled={disabled || loading}
      className={classes}
      {...props}
    >
      {loading ? (
        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
      ) : (
        <>
          {icon && <span>{icon}</span>}
          {children}
        </>
      )}
    </button>
  );
};

export const InputField = ({
  name,
  label,
  type = "text",
  placeholder = "",
  required = false,
  disabled = false,
  readOnly = false,
  value,
  onChange,
  error,
  icon = null,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const isPassword = type === "password";
  const displayType = isPassword && showPassword ? "text" : type;

  const toggleVisibility = () => setShowPassword((prev) => !prev);

  return (
    <div className="mb-4">
      {label && (
        <label htmlFor={name} className="block mb-1 font-medium text-xs text-light-text dark:text-dark-text">
          {label}
        </label>
      )}

      <div className="relative">
        {icon && <div className="absolute left-3 top-2.5 text-gray-400">{icon}</div>}
        <input
          id={name}
          name={name}
          type={displayType}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          {...props}
          className={`w-full px-4 py-2 rounded-md text-sm text-light-text dark:text-dark-text
            border focus:outline-none focus:ring-2 transition-all
            ${icon ? 'pl-10' : ''}
            ${error ? 'border-red-500 focus:ring-red-400' : 'border-gray-300 focus:ring-light-secondary/20 dark:focus:ring-dark-secondary/20'}
            dark:bg-gray-700 dark:text-white dark:border-gray-600`}
        />

        {/* 👁️ Toggle mot de passe */}
        {isPassword && (
          <button
            type="button"
            onClick={toggleVisibility}
            className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 dark:text-gray-300"
          >
            {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
          </button>
        )}
      </div>

      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};


export const SelectField = ({
  name,
  label,
  value,
  onChange,
  options = [],
  required = false,
  disabled = false,
  readOnly = false,
  error,
  placeholder = '—',
  ...props
}) => {
  return (
    <div className="mb-4">
      {label && (
        <label htmlFor={name} className="block mb-1 font-medium text-xs text-light-text dark:text-dark-text">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          {...props}
          className={`w-full px-4 py-2 rounded-md text-sm text-light-text dark:text-white
            border focus:outline-none focus:ring-2 transition-all
            ${error ? 'border-red-500 focus:ring-red-400' : 'border-gray-300 focus:ring-light-secondary/20 dark:focus:ring-dark-secondary/20'}
            dark:bg-gray-700 dark:border-gray-600`}
        >
          <option value="" disabled>{placeholder}</option>
          {options.map(({ value, label }, idx) => (
            <option key={idx} value={value}>{label}</option>
          ))}
        </select>
      </div>
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};


export const TextAreaField = ({
  name,
  label,
  placeholder = "",
  required = false,
  disabled = false,
  readOnly = false,
  rows = 4,
  value,
  onChange,
  error,
  icon = null,
}) => {

  return (
    <div className="mb-4">
      {label && (
        <label htmlFor={name} className="block mb-1 font-medium text-xs text-light-text dark:text-dark-text">
          {label}
        </label>
      )}

      <div className="relative">
        {icon && <div className="absolute left-3 top-3 text-gray-400">{icon}</div>}
        <textarea
          id={name}
          name={name}
          rows={rows}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          className={`w-full px-4 py-2 rounded-md text-sm text-light-text dark:text-dark-text
            border focus:outline-none focus:ring-2 transition-all resize-none
            ${icon ? 'pl-10' : ''}
            ${error ? 'border-red-500 focus:ring-red-400' : 'border-gray-300 focus:ring-light-secondary/20 dark:focus:ring-dark-secondary/20'}
            dark:bg-gray-700 dark:text-white dark:border-gray-600`}
        />
      </div>

      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};

export const ThemedCheckbox = ({
  label,
  checked,
  onChange,
  name,
  id,
  disabled = false,
}) => {
  return (
    <label
      htmlFor={id}
      className={`flex items-center cursor-pointer select-none ${disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
    >
      <input
        type="checkbox"
        name={name}
        id={id}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="peer hidden"
      />
      <div
        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center
          transition-colors duration-300
          bg-white dark:bg-gray-800
          border-light-primary/40 dark:border-dark-primary/40
          peer-checked:bg-light-primary dark:peer-checked:bg-dark-primary
        `}
      >
        {/* ✅ checkmark */}
        <svg
          className="w-3 h-3 text-white"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <span className="ml-2 text-sm text-light-text dark:text-dark-text">
        {label}
      </span>
    </label>
  );
};


export const FieldsetGroup = ({
  legend,
  description,
  children,
  error,
}) => {
  return (
    <fieldset
      className={`mb-4 px-4 py-3 rounded-md border
        ${error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}
        bg-white dark:bg-gray-700 text-light-text dark:text-white`}
    >
      {legend && (
        <legend className="px-2 font-semibold text-sm text-light-text dark:text-dark-text">
          {legend}
        </legend>
      )}
      {description && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{description}</p>
      )}
      <div className="space-y-3">{children}</div>
      {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
    </fieldset>
  );
};




export const OtpField = ({
  value,
  onChange,
  numInputs = 6,
  disabled = false,
  error = false,
  autoFocus = true,
}) => {
  return (
    <OTPInput
      value={value}
      onChange={onChange}
      numInputs={numInputs}
      inputType="tel"
      isInputNum
      shouldAutoFocus={autoFocus}
      containerStyle="flex justify-between gap-4"
      renderInput={(props, index) => (
        <input
          {...props}
          key={index}
          className={`
            w-12 h-12 rounded-md text-center text-lg font-semibold
            border focus:outline-none focus:ring-2 transition-all
            border-light-primary/40 dark:border-dark-primary/40
            focus:ring-light-secondary/20 dark:focus:ring-dark-secondary/20
            bg-white dark:bg-gray-700
            text-light-text dark:text-dark-text
          `}
          style={{
            width: '90% !important',
            height: '90% !important'
          }}
        />
      )}
    />
  );
};



